import { CircleCheck, MoreVerticalIcon, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatLocalizedDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { CorrectionRequest } from '@/types/core/purchase-order/correction-request';

export type ProductionDeclarationMaterialCommentCardVariant =
  | 'resolved'
  | 'supplier'
  | 'unresolved';

type ProductionDeclarationMaterialCommentCardProps = {
  comment: CorrectionRequest;
  isUpdatingCommentResolution: boolean;
  locale: string;
  onCommentResolutionChange?: (comment: CorrectionRequest) => void;
  onDeleteClick?: (comment: CorrectionRequest) => void;
  onEditClick?: (comment: CorrectionRequest) => void;
  variant: ProductionDeclarationMaterialCommentCardVariant;
};

export function ProductionDeclarationMaterialCommentCard({
  comment,
  isUpdatingCommentResolution,
  locale,
  onCommentResolutionChange,
  onDeleteClick,
  onEditClick,
  variant,
}: ProductionDeclarationMaterialCommentCardProps) {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const { t: tCommon } = useTranslation('common', {
    useSuspense: false,
  });
  const createdAtTime = new Date(comment.createdAt).getTime();
  const updatedAtTime = comment.updatedAt
    ? new Date(comment.updatedAt).getTime()
    : createdAtTime;
  const isUpdated = updatedAtTime !== createdAtTime;
  const displayedDate = isUpdated ? comment.updatedAt : comment.createdAt;
  const resolutionActionLabel = t(
    comment.isResolved
      ? 'materialCommodityOrigins.comments.markAsUnresolved'
      : 'materialCommodityOrigins.comments.markAsResolved',
  );
  const hasActions = Boolean(onEditClick || onDeleteClick);

  return (
    <article
      className={cn(
        'rounded-lg border px-4 py-3 text-declaration-foreground',
        variant === 'resolved' && 'border-emerald-600 bg-emerald-50/70',
        variant === 'supplier' && 'border-red-500 bg-red-50/70',
        variant === 'unresolved' && 'border-orange-500 bg-orange-50/60',
      )}
    >
      <div className='flex items-start gap-3'>
        <div className='min-w-0 flex-1 space-y-3'>
          <div className='flex flex-wrap items-center gap-x-2 gap-y-1 text-sm'>
            <div className='flex items-center gap-2 font-semibold text-declaration-muted-foreground'>
              <UserRound aria-hidden className='size-4 shrink-0' />
              <span>{t('materialCommodityOrigins.comments.author')}</span>
            </div>
            <span className='text-declaration-muted-foreground'>
              {isUpdated
                ? `${t('materialCommodityOrigins.comments.updatedAt')} `
                : null}
              {formatLocalizedDateTime(displayedDate, locale)}
            </span>
          </div>
          <p className='whitespace-pre-wrap text-sm leading-6'>
            {comment.comment}
          </p>
        </div>
        {onCommentResolutionChange ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label={resolutionActionLabel}
                className='rounded-sm p-1 text-declaration-foreground transition-colors hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-50'
                disabled={isUpdatingCommentResolution}
                onClick={() => onCommentResolutionChange(comment)}
                type='button'
              >
                <CircleCheck aria-hidden className='size-5' />
              </button>
            </TooltipTrigger>
            <TooltipContent>{resolutionActionLabel}</TooltipContent>
          </Tooltip>
        ) : null}
        {hasActions ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                aria-label={t('materialCommodityOrigins.comments.actions')}
                className='rounded-sm p-1 text-declaration-foreground transition-colors hover:text-declaration-muted-foreground'
                type='button'
              >
                <MoreVerticalIcon aria-hidden className='size-5' />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-40'>
              {onEditClick ? (
                <DropdownMenuItem onClick={() => onEditClick(comment)}>
                  {tCommon('edit')}
                </DropdownMenuItem>
              ) : null}
              {onDeleteClick ? (
                <DropdownMenuItem onClick={() => onDeleteClick(comment)}>
                  {tCommon('delete')}
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </article>
  );
}
